'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import {
  inputClass,
  labelClass,
  formContainerClass,
  errorContainerClass,
  primaryButtonClass,
  formGroupClass,
  fieldContainerClass
} from '@/components/ui/FormStyles';

// Animated background component
const AnimatedBackground = () => {
  useEffect(() => {
    // Create animated shapes
    const createShapes = () => {
      const container = document.getElementById('animated-bg');
      if (!container) return;
      
      // Clear existing shapes
      container.innerHTML = '';
      
      // Create different shapes
      const shapes = ['square', 'circle', 'triangle', 'diamond'];
      
      for (let i = 0; i < 20; i++) {
        const shape = document.createElement('div');
        const size = Math.random() * 80 + 30;
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        const duration = Math.random() * 30 + 15;
        const delay = Math.random() * 8;
        const opacity = Math.random() * 0.2 + 0.05;
        const rotation = Math.random() * 360;
        const shapeType = shapes[Math.floor(Math.random() * shapes.length)];
        
        shape.className = `animated-shape ${shapeType}`;
        shape.style.cssText = `
          width: ${size}px;
          height: ${size}px;
          left: ${posX}%;
          top: ${posY}%;
          animation-duration: ${duration}s;
          animation-delay: ${delay}s;
          opacity: ${opacity};
          transform: rotate(${rotation}deg);
          background-color: ${Math.random() > 0.5 ? '#2563eb' : '#4f46e5'};
        `;
        
        container.appendChild(shape);
      }
    };
    
    createShapes();
    window.addEventListener('resize', createShapes);
    
    return () => {
      window.removeEventListener('resize', createShapes);
    };
  }, []);
  
  return (
    <div id="animated-bg" className="fixed inset-0 overflow-hidden pointer-events-none z-0" />
  );
};

export default function SignUp() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    organization: '',
    designation: '',
    tshirtSize: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          organization: formData.organization,
          designation: formData.designation,
          tshirtSize: formData.tshirtSize,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      // Redirect to sign in page
      router.push('/auth/signin?registered=true');
    } catch (error) {
      setError(error.message || 'An error occurred during registration');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      {/* Animated background */}
      <AnimatedBackground />
      
      {/* Animation styles */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          33% {
            transform: translateY(-30px) translateX(30px) rotate(15deg);
          }
          66% {
            transform: translateY(30px) translateX(-30px) rotate(-15deg);
          }
          100% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
        }
        
        .animated-shape {
          position: absolute;
          filter: blur(20px);
          animation: float infinite ease-in-out;
          z-index: -1;
        }
        
        .circle {
          border-radius: 50%;
        }
        
        .square {
          border-radius: 10%;
        }
        
        .triangle {
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
        }
        
        .diamond {
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
        }
      `}</style>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="text-center">
          <Link href="/" className="inline-block">
            <Image 
              src="/bdnog.svg" 
              alt="BDNOG Logo" 
              width={200} 
              height={60} 
              priority
            />
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Create your account</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link href="/auth/signin" className="font-medium text-blue-600 hover:text-blue-500">
            sign in to your existing account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`${formContainerClass} bg-gray-100`}>
          {error && (
            <div className={errorContainerClass}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form className={formGroupClass} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
              <div className="md:col-span-2">
                <label htmlFor="name" className={labelClass}>
                  Full Name
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className={labelClass}>
                  Email address
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className={labelClass}>
                  Phone Number
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="organization" className={labelClass}>
                  Organization (Optional)
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    autoComplete="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your organization"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="designation" className={labelClass}>
                  Designation (Optional)
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="designation"
                    name="designation"
                    type="text"
                    autoComplete="organization-title"
                    value={formData.designation}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your designation"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="tshirtSize" className={labelClass}>
                  T-Shirt Size
                </label>
                <div className={fieldContainerClass}>
                  <select
                    id="tshirtSize"
                    name="tshirtSize"
                    value={formData.tshirtSize}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select a size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="XXXL">XXXL</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    View Size Guide
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="password" className={labelClass}>
                  Password
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Create a password"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className={labelClass}>
                  Confirm Password
                </label>
                <div className={fieldContainerClass}>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Confirm your password"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center ${primaryButtonClass}`}
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowSizeGuide(false)}
            ></div>

            {/* Modal panel */}
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full z-10">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                      T-Shirt Size Guide
                    </h3>
                    <div className="mt-2">
                      <Image 
                        src="/sizechart.jpeg" 
                        alt="T-Shirt Size Chart" 
                        width={800}
                        height={600}
                        className="w-full h-auto rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowSizeGuide(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
